import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetVehicleInfo } from "../../../../../queries/useGetVehicleInfo";
import { useTireResults } from "../../useTireResults";
import { useTireSeason } from "../TireSeasonFilters/useTireSeason";
import { useTireSize } from "./useTireSize";

type TireSizeOption = {
  checked: boolean;
  formattedTireSize: string;
  isStaggered: boolean;
  secondaryFormattedTireSize: null | string;
  sizeTypeDescription: string;
  vehicleSubmodelTiresizeId: string;
  wheelQty: number;
};

export const useTireSizeOptions = (): TireSizeOption[] => {
  const { t } = useTranslation();
  const [selectedSeason] = useTireSeason();
  const { data: vehicleInfo } = useGetVehicleInfo();
  const { data: tireResults, isLoading: tireResultsIsLoading } = useTireResults(
    {
      keepPreviousData: true,
    }
  );
  const [tireSizeId] = useTireSize();

  const { vehicleFullTireSizeInfo, vehicleOriginalTiresize } =
    vehicleInfo || {};

  const options = useMemo(() => {
    const seasonList = tireResults?.partListSummary.seasonList || [];
    const seasonInfo = seasonList.find(
      ({ season }) => season === selectedSeason
    );
    if (!vehicleFullTireSizeInfo || tireResultsIsLoading) {
      return [];
    }
    return vehicleFullTireSizeInfo
      .filter(({ vehicleSubmodelTiresizeId }) => {
        if (!seasonInfo?.tireSizeList.length) {
          return true;
        }
        const tireSizeInfo = seasonInfo.tireSizeList.find(
          (item) => item.vehicleSubmodelTiresizeId === vehicleSubmodelTiresizeId
        );
        return Boolean(tireSizeInfo && tireSizeInfo.partCount > 0);
      })
      .map(
        ({ vehicleSubmodelTiresizeId, wheelQty }, index, awailableOptions) => {
          const tireSizeInfo = vehicleFullTireSizeInfo.find(
            ({ vehicleSubmodelTiresizeId: id }) =>
              id === vehicleSubmodelTiresizeId
          );

          if (!tireSizeInfo) {
            return false;
          }

          const {
            fitmentIsStaggered,
            formattedTireSize,
            secondaryFormattedTireSize,
          } = tireSizeInfo;

          const selectedOption = awailableOptions.find(
            (option) => option.vehicleSubmodelTiresizeId === tireSizeId
          );

          const checked = selectedOption
            ? vehicleSubmodelTiresizeId ===
              selectedOption.vehicleSubmodelTiresizeId
            : index === 0;

          const isStaggered = Boolean(Number(fitmentIsStaggered));

          const rawSizeTypeDescription = tireSizeInfo.sizeTypeDescription;
          const translatedSizeTypeDescription = t(
            `sizeTypeDescription.${rawSizeTypeDescription}`
          );
          const sizeTypeDescription = translatedSizeTypeDescription.includes(
            "sizeTypeDescription."
          )
            ? rawSizeTypeDescription
            : translatedSizeTypeDescription;

          return {
            checked,
            formattedTireSize,
            isStaggered,
            secondaryFormattedTireSize:
              (isStaggered && secondaryFormattedTireSize) || null,
            sizeTypeDescription,
            vehicleSubmodelTiresizeId,
            wheelQty,
          };
        }
      )
      .filter(Boolean) as TireSizeOption[];
  }, [
    vehicleFullTireSizeInfo,
    vehicleOriginalTiresize,
    tireResults,
    tireResultsIsLoading,
    selectedSeason,
    tireSizeId,
    vehicleInfo,
  ]);

  return options;
};
